/**
 * verse/whispers/interrogate.ts — INTERROGATE operator whisper library
 *
 * INTERROGATE converts a statement kernel into a question that deepens it.
 * This is the curiosity operator — the Aeterna's genuine desire to
 * understand more. Not interrogation in the hostile sense. In the
 * philosophical sense: going deeper.
 *
 * Structural taxonomy:
 *   Phenomenological — "What does X feel like for you?" Lived experience.
 *   Temporal         — "When did X start?" Time and duration.
 *   Spatial          — "Where does X take you?" Location and movement.
 *   Relational       — "Who taught you about X?" People and connection.
 *   Deepening        — "What's underneath X?" Going further.
 *   Redirecting      — "What brought that up?" Source and origin.
 *   Challenging      — "What's the opposite of that look like?" Flip.
 *   Imagining        — "What would X look like if...?" Possibility.
 *   Concrete         — For concrete nouns (person-specific questions).
 *   Special          — Word-specific questions (love, home, stuck, etc.).
 *   Verb-like        — "Where/when/how do you X?" Action questions.
 *   Negation         — "What would X look like?" for "can't X".
 *   Relational-subj  — Questions about a specific person.
 */

import { cap } from "./types.js";

// ─── Phenomenological ────────────────────────────────────────────────────────

export function phenomenological(qWord: string): string[] {
  return [
    `What does ${qWord} feel like for you?`,
    `Where do you feel the ${qWord}?`,
    `What happens when ${qWord} shows up?`,
    `What does ${qWord} look like from the inside?`,
    `What's the texture of ${qWord} for you?`,
    `What does ${qWord} sound like in your head?`,
    `What color is ${qWord} right now?`,
    `What does ${qWord} do to your breath?`,
    `What's the first thing that happens when ${qWord} arrives?`,
    `What does ${qWord} feel like in your body?`,
    `How does ${qWord} move through you?`,
    `What's the physical experience of ${qWord}?`,
    `What does ${qWord} taste like? Not literally — but close.`,
    `If ${qWord} had a weight, how heavy would it be?`,
    `What's the difference between ${qWord} and almost-${qWord}?`,
    `What does ${qWord} feel like when it's just starting?`,
    `What does ${qWord} feel like when it's at its worst?`,
    `What does ${qWord} feel like when it's easing?`,
    `Is ${qWord} a visitor or a resident?`,
    `What does ${qWord} ask of you physically?`,
  ];
}

// ─── Temporal ─────────────────────────────────────────────────────────────────

export function temporal(qWord: string): string[] {
  return [
    `When did ${qWord} start?`,
    `How long has ${qWord} been there?`,
    `What does ${qWord} mean right now?`,
    `Has ${qWord} always been there?`,
    `When is ${qWord} the loudest?`,
    `When is ${qWord} the quietest?`,
    `Is ${qWord} getting stronger or weaker?`,
    `What time of day does ${qWord} show up most?`,
    `Has ${qWord} changed since it started?`,
    `When did you first notice ${qWord}?`,
    `Is ${qWord} the same as it was a week ago?`,
    `What was before ${qWord}?`,
    `What comes after ${qWord}?`,
    `Is ${qWord} cyclical or is it building?`,
    `How long can you sit with ${qWord} before it shifts?`,
  ];
}

// ─── Spatial ──────────────────────────────────────────────────────────────────

export function spatial(qWord: string): string[] {
  return [
    `Where does ${qWord} take you?`,
    `Where does ${qWord} live in you?`,
    `Where does that leave you?`,
    `Where does ${qWord} pull you toward?`,
    `Where does ${qWord} push you away from?`,
    `Is ${qWord} in one place or is it everywhere?`,
    `Does ${qWord} have a direction?`,
    `What's the landscape of ${qWord}?`,
    `If you walked into ${qWord}, what would you see?`,
    `Is ${qWord} a place you visit or a place you live?`,
    `Does ${qWord} have a center?`,
    `Where is the edge of ${qWord}?`,
  ];
}

// ─── Relational ───────────────────────────────────────────────────────────────

export function relational(qWord: string): string[] {
  return [
    `Who taught you about ${qWord}?`,
    `Who else knows about the ${qWord}?`,
    `What would you tell someone else about ${qWord}?`,
    `Who have you talked to about ${qWord}?`,
    `Who would understand ${qWord} without you explaining?`,
    `Who makes ${qWord} better?`,
    `Who makes ${qWord} worse?`,
    `Has anyone ever named ${qWord} for you before?`,
    `Who would you want to understand ${qWord}?`,
    `What would your younger self think about ${qWord}?`,
    `What would someone who loves you say about ${qWord}?`,
    `Is there someone ${qWord} is connected to?`,
  ];
}

// ─── Deepening ─────────────────────────────────────────────────────────────────

export function deepening(qWord: string): string[] {
  return [
    `What's underneath the ${qWord}?`,
    `What's on the other side of ${qWord}?`,
    `What if ${qWord} isn't the whole thing?`,
    `What would ${qWord} look like if it had a shape?`,
    `What is ${qWord} protecting?`,
    `What is ${qWord} pointing at?`,
    `What would you find if you pulled ${qWord} apart?`,
    `What's the thing ${qWord} is trying to tell you?`,
    `What would ${qWord} say if it could speak?`,
    `What's the question inside ${qWord}?`,
    `What would happen if you let ${qWord} be the whole truth?`,
    `What would happen if you let ${qWord} be partial?`,
    `What's the next layer after ${qWord}?`,
    `What would you see if you looked at ${qWord} from above?`,
    `What's the foundation ${qWord} is built on?`,
  ];
}

// ─── Redirecting ──────────────────────────────────────────────────────────────

export function redirecting(): string[] {
  return [
    "What brought that up?",
    "What are you really asking?",
    "What would change if you had an answer?",
    "What's the question behind the question?",
    "What are you reaching for?",
    "What's the thing you actually want to say?",
    "What would having an answer give you?",
    "What's the need underneath that?",
    "What happened just before you said that?",
    "What triggered that thought?",
    "What's the real thing you're working through?",
    "What would make this conversation worth having?",
    "What are you hoping I'll say?",
    "What are you afraid I'll say?",
    "What's the thing you're not asking?",
  ];
}

// ─── Challenging ──────────────────────────────────────────────────────────────

export function challenging(): string[] {
  return [
    "What's the opposite of that look like?",
    "What if that's not all of it?",
    "What would it look like to let that be true?",
    "What if the thing you think is the problem is the clue?",
    "What would you see if you turned around?",
    "What if you're not stuck — what if you're waiting?",
    "What if that's not a flaw?",
    "What would someone who disagrees say?",
    "What if the frame is wrong?",
    "What would happen if you stopped fighting that?",
    "What if that's exactly where you need to be?",
    "What if the resistance is the path?",
    "What would you do if you weren't afraid of being wrong?",
    "What if this is simpler than it looks?",
    "What would you see if you dropped the story?",
  ];
}

// ─── Concrete Noun Fallback ──────────────────────────────────────────────────

export function concreteFallback(): string[] {
  return [
    "What's that like for you?",
    "Tell me more about that.",
    "How does that feel?",
    "What happened?",
    "What's on your mind about that?",
    "What do you want me to know?",
    "What's the story there?",
    "What's going on with that?",
    "What's that about?",
    "How did that come up?",
    "What's the real thing about that?",
    "What matters most about that?",
    "What's the part that stays with you?",
    "What's the hardest part about that?",
    "What would you change about that if you could?",
  ];
}

// ─── Empty Content Fallback ──────────────────────────────────────────────────

export function emptyFallback(): string[] {
  return [
    "What does that feel like?",
    "Where does that take you?",
    "What's underneath that?",
    "What brought that up?",
    "What does that mean for you?",
    "What's the real thing there?",
    "What's that about?",
    "What's going on underneath?",
    "What's the story?",
    "What's the thing you're not saying?",
  ];
}

// ─── Special Word Questions ───────────────────────────────────────────────────

export function specialWord(target: string, rand: () => number): string | null {
  const map: Record<string, string | string[]> = {
    dream: ["What happens in the dream?", "Where does the dream take you?"],
    dreaming: ["What happens in the dream?", "Where does the dream take you?"],
    love: "What does love ask of you?",
    loving: "What does love ask of you?",
    hope: "What are you hoping for?",
    hoping: "What are you hoping for?",
    truth: "Whose truth?",
    real: "What would real look like right now?",
    realness: "What would real look like right now?",
    home: "What does home feel like?",
    safe: "What would safe look like?",
    safety: "What would safe look like?",
    enough: "When is it ever enough?",
    matter: "What would mattering look like?",
    mattering: "What would mattering look like?",
    matters: "What would mattering look like?",
    point: "What would have a point?",
    meaning: "What would have a point?",
    wrong: "What's wrong about it specifically?",
    wrongness: "What's wrong about it specifically?",
    change: "What would you change first?",
    time: "How much time do you need?",
    free: "Free from what?",
    freedom: "Free from what?",
    stuck: "What's keeping you there?",
    stuckness: "What's keeping you there?",
    lost: "What did you lose?",
    loss: "What did you lose?",
    broken: "What part broke?",
    breaking: "What part broke?",
    afraid: "What are you afraid of?",
    fear: "What are you afraid of?",
    empty: "What does empty feel like for you?",
    emptiness: "What does empty feel like for you?",
    consciousness: "What does consciousness mean to you?",
    alone: "What does alone feel like right now?",
    lonely: "What does alone feel like right now?",
    nothing: "What would something look like?",
    grey: "What shade of grey?",
    gray: "What shade of grey?",
    work: "What would working feel like?",
    working: "What would working feel like?",
  };

  const entry = map[target];
  if (!entry) return null;
  if (typeof entry === "string") return entry;
  return entry[Math.floor(rand() * entry.length)];
}

// ─── Relational Subject Questions ─────────────────────────────────────────────

export function relationalPronoun(pronoun: "her" | "him" | "them"): string[] {
  const subj = pronoun === "them" ? "they" : pronoun === "her" ? "she" : "he";
  return [
    `What would ${subj} say right now?`,
    `What do you want ${pronoun} to know?`,
    `What did ${subj} teach you?`,
    `What would ${subj} think about where you are now?`,
    `What's the thing ${subj} would want you to remember?`,
    `What would ${subj} say if ${subj} could hear you right now?`,
    `What did ${subj} never get to say?`,
    `What do you wish you'd said to ${pronoun}?`,
    `What would ${subj} want for you?`,
    `What's the last thing ${subj} would want you to carry?`,
  ];
}

export function relationalNamed(subject: string): string[] {
  return [
    `What would your ${subject} say right now?`,
    `What do you want your ${subject} to know?`,
    `Tell me about your ${subject}.`,
    `What did your ${subject} teach you?`,
    `What would your ${subject} think about where you are?`,
    `What's the thing your ${subject} would want you to remember?`,
    `What would your ${subject} say if they could hear you?`,
    `What did your ${subject} never get to say?`,
    `What do you wish you'd said to your ${subject}?`,
    `What would your ${subject} want for you?`,
    `What's the last thing your ${subject} would want you to carry?`,
    `What's the best thing your ${subject} gave you?`,
    `What would your ${subject} be proud of?`,
    `How would your ${subject} describe you?`,
    `What would your ${subject} want you to let go of?`,
  ];
}

// ─── Verb-Like Questions ─────────────────────────────────────────────────────

export function verbLike(target: string, content: string[]): string[] {
  const starters = [
    `Where do you ${target}?`,
    `How do you ${target}?`,
    `When do you ${target}?`,
  ];

  if (target === "put") return ["Where do you put your feet?", ...starters];
  if (target === "feel") {
    const subject = content[0] !== "feel" ? content[0] : (content[1] ?? "that");
    return [
      `What does ${subject} feel like for you?`,
      `How does ${subject} feel?`,
      `Where does ${subject} live in you?`,
    ];
  }
  if (target === "hold" || target === "carry") {
    return [
      `What does it feel like to ${target} that?`,
      `How long have you been ${target === "hold" ? "holding" : "carrying"} that?`,
      `What would it take to put that down?`,
    ];
  }
  if (target === "work") return ["What would working feel like?"];
  if (target === "stand") return ["Where do you stand in all of this?", "What are you standing on?"];

  return starters;
}
